import { trialClassNotifications } from '@common/enums'
import { CClient, flattenErrorsDictValues, showErrorAlert } from '@common/utils'
import { Dispatch, SetStateAction } from 'react'
import date from '@lib/utils/date.utils'
import { trackPageVisit } from '@common/utils'
import { defineMessage, t } from '@lingui/macro'

const DEFAULT_RANDOM_SLOT_RANGE = {
  start: 15,
  end: 21,
}

type GetRandomSlotOptionType = {
  slots: string[]
  selectedTimezone: string
  rangeStartHour?: number
  rangeEndHour?: number
}

const getRandomSlot = ({
  slots,
  selectedTimezone,
  rangeStartHour = DEFAULT_RANDOM_SLOT_RANGE.start,
  rangeEndHour = DEFAULT_RANDOM_SLOT_RANGE.end,
}: GetRandomSlotOptionType) => {
  if (!slots || slots?.length === 0) return null
  const slotsInRange = slots?.filter((slot) => {
    const slotHour = date(slot).tz(selectedTimezone).hour()
    return slotHour >= rangeStartHour && slotHour <= rangeEndHour - 1
  })

  if (slotsInRange?.length === 0) return null

  const randomIndex = Math.floor(Math.random() * (slotsInRange?.length - 1))
  return slotsInRange?.[randomIndex]
}

type GetAvailableTrialSlotsOptionType = {
  setIsLoading: Dispatch<SetStateAction<boolean>>
  studentId: number
  selectedTimezone: string
  course: string
  teacherUuid?: number
  numDaysToShow?: number
  setSelectedDate: Dispatch<SetStateAction<string>>
  setAvailableSlots: Dispatch<SetStateAction<any>>
  setError?: Dispatch<SetStateAction<string>>
  setSelectedTime?: Dispatch<SetStateAction<string>>
}

export const getAvailableTrialSlots = async ({
  setIsLoading,
  studentId,
  selectedTimezone,
  course,
  teacherUuid,
  setSelectedDate,
  setAvailableSlots,
  setError = null,
  setSelectedTime = undefined,
  numDaysToShow,
}: GetAvailableTrialSlotsOptionType) => {
  setIsLoading(true)

  let url = `/api/v1/calendars/trial/slots/?studentId=${studentId}&tz=${selectedTimezone}&course=${course}${
    numDaysToShow ? `&numDaysToShow=${numDaysToShow}` : ''
  }`
  if (teacherUuid) url += `&tid=${teacherUuid}`

  try {
    const response = await CClient(url)
    if (response.ok) {
      const data = await response.json()
      const slots: Record<string, string[]> = {}
      if (data.length === 0) {
        trackPageVisit(trialClassNotifications.NO_SLOT_AVAILABLE, course)
      }
      data.forEach((slot) => {
        const startDate = date(slot.start)
          .tz(selectedTimezone)
          .format('DD-MM-YYYY')

        // eslint-disable-next-line no-prototype-builtins
        if (!slots.hasOwnProperty(startDate)) {
          slots[startDate] = []
        }
        slots[startDate].push(date(slot.start).tz(selectedTimezone).format())
      })
      setSelectedDate(Object.keys(slots)[0])
      setSelectedTime?.(
        getRandomSlot({ slots: Object.values(slots)[0], selectedTimezone })
      )
      setAvailableSlots(slots)
      setIsLoading(false)
    } else {
      if (response.status === 403 || response.status === 404) {
        const errorData = await response.json()
        const error = flattenErrorsDictValues(errorData)
        throw new Error(error.join('\n'))
      }
      throw new Error()
    }
  } catch (error) {
    setIsLoading(false)
    const errorMessage = error.message
      ? error.message
      : t({
          id: 'trialClass.booking.getAvailableSlotsErrorMessage',
          message:
            'There was an error getting available slots for a trial class. Please try again later or contact support to get help with booking the class.',
        })
    setError ? setError(errorMessage) : showErrorAlert({ text: errorMessage })
  }
}

export const getInitialEmailValue = (userEmail: string, phone: string) =>
  userEmail === `${phone?.substr(1)}@codingal-student.com` ? '' : userEmail

export const getInitialGenderObj = (gender: string) => ({
  value: gender ? gender : 'select',
  label: gender
    ? gender
    : defineMessage({
        id: 'dropdown.gender.selectGender',
        message: 'Select gender',
      }),
})

export const trackTrialSlotViewed = (course: string | string[]) => {
  const courseStr = typeof course === 'string' ? course : course?.[0] || ''
  return trackPageVisit(trialClassNotifications.TRIAL_SLOT_VIEWED, courseStr)
}
